import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'

export interface CampaignWithStats {
  id: string
  name: string
  slug: string
  description: string | null
  campaign_type: string
  is_active: boolean
  starts_at: string | null
  ends_at: string | null
  created_at: string
  total_submissions: number
  avg_score: number | null
  maturity_distribution: Record<string, number>
}

export interface Submission {
  id: string
  campaign_id: string
  lead_id: string | null
  company_name: string
  cnpj: string | null
  sector: string | null
  employee_count: number | null
  contact_name: string
  contact_email: string
  contact_role: string | null
  score_strategy: number | null
  score_governance: number | null
  score_social: number | null
  score_environmental: number | null
  score_overall: number | null
  maturity_level: string | null
  utm_source: string | null
  created_at: string
}

export interface SubmissionList {
  items: Submission[]
  total: number
  page: number
  size: number
}

export interface CampaignDashboard {
  campaign: CampaignWithStats
  total_submissions: number
  avg_score_overall: number | null
  avg_score_strategy: number | null
  avg_score_governance: number | null
  avg_score_social: number | null
  avg_score_environmental: number | null
  maturity_distribution: Record<string, number>
  sector_distribution: Record<string, number>
  submissions_by_day: Array<{ date: string; count: number }>
  lead_conversion_count: number
}

export function useCampaigns() {
  return useQuery({
    queryKey: ['campaigns'],
    queryFn: async () => {
      const { data } = await api.get<CampaignWithStats[]>('/v1/campaigns')
      return data
    },
  })
}

export function useCampaignDashboard(campaignId: string) {
  return useQuery({
    queryKey: ['campaign-dashboard', campaignId],
    queryFn: async () => {
      const { data } = await api.get<CampaignDashboard>(`/v1/campaigns/${campaignId}/dashboard`)
      return data
    },
    enabled: !!campaignId,
  })
}

export function useCampaignSubmissions(campaignId: string, params: { page?: number; size?: number; search?: string; maturity?: string } = {}) {
  return useQuery({
    queryKey: ['campaign-submissions', campaignId, params],
    queryFn: async () => {
      const query = new URLSearchParams()
      if (params.page) query.set('page', String(params.page))
      if (params.size) query.set('size', String(params.size))
      if (params.search) query.set('search', params.search)
      if (params.maturity) query.set('maturity', params.maturity)
      const { data } = await api.get<SubmissionList>(`/v1/campaigns/${campaignId}/submissions?${query}`)
      return data
    },
    enabled: !!campaignId,
  })
}
